import type { CellChange, Command, TileCellChange } from "../document/commands.ts";
import { packedToTileRef, tileRefToPacked } from "../document/packed-tile.ts";
import type { EntityInstance, JsonValue, LayerDocument, MapDocument, PropInstance, WorldDocument } from "../document/types.ts";
import { zoneTypeAt, type ZoneCellChange } from "../document/zones.ts";
import { anchoredIn, areaGridBounds, areaGridSize, type AreaGrid, type AreaRect } from "./area.ts";
import type { AreaClipLayer, AreaClipboard } from "./area-clipboard.ts";
import { eachCell, isGridLayer, rawGridOf, type GridLayer } from "./area-grids.ts";
import { areaMapOf, edgeOf, type AreaEdge, type AreaKept, type AreaKeptThing, type AreaReplaced } from "./area-move.ts";
import { referenceFieldsOf, type ReferenceTypes } from "./references.ts";
import type { JsonSchema } from "./schema.ts";

/**
 * The paste of an area's clipboard within one map (ADR-128 decisions 37 to 39): one `compound` of the commands that
 * exist, built against the document as it stands when the builder confirms. Each layer the clipboard names and the
 * reach still takes lands where the block is put, cell for cell: one `setTiles`, `paintCorners`, `paintCollision`
 * or `paintZone` for each grid layer, carrying only the cells whose value changes, then one `addProp` or `addEntity`
 * for each prop and marker, appended to its layer with a new id and nothing else changed. A block that would leave
 * the map is refused (decision 24). Paint already at the destination is replaced, never refused, as a move's is
 * (decisions 19 and 20): the source is not emptied, so every cell of the destination that holds paint and that the
 * block would change counts in `replaced`. Props and markers already at the destination never refuse a paste and
 * stay, named in `kept`.
 *
 * A reference field a registered type declares holds the id of a prop or a marker of the map. A pasted thing whose
 * field names a thing that is pasted too names the copy; one that names a thing that is not pasted keeps naming the
 * original (decision 38). A terrain block's outer ring of corners follows decision 21 at the destination: an edge
 * corner the block holds as empty does not clear a corner that is set there. Door layers are never in a clipboard.
 */

/**
 * What a paste mints ids with: the store's allocator, which hands back `count` ids for a prefix that no layer,
 * prop or marker of the world holds. It is the one impure thing a paste reads, so a plan made only to be shown
 * passes a counter of its own and the confirmed paste the store's.
 */
export type AreaIds = (prefix: "prop" | "entity", count: number) => readonly string[];

/**
 * A planned paste: nothing to do; refused at the map's edge, with how far; or the command with the layers it writes in
 * the map's order, the cell changes it carries, the props and markers it adds, the painted destination cells it
 * replaces, the things already standing at the destination and the clipboard's layers it left behind (out of reach
 * now, or no longer on the map).
 */
export type AreaPastePlan =
  | { readonly kind: "nothing" }
  | { readonly kind: "refused"; readonly edge: AreaEdge }
  | {
      readonly kind: "paste";
      readonly command: Extract<Command, { readonly kind: "compound" }>;
      readonly layers: readonly string[];
      readonly changes: number;
      readonly props: number;
      readonly markers: number;
      readonly replaced: readonly AreaReplaced[];
      readonly kept: AreaKept;
      readonly left: readonly string[];
    };

/**
 * Whether a clipboard layer's block fits the kind of layer it names now.
 *
 * @param entry The block.
 * @param layer The layer of that id on the map.
 * @returns True when they are of one kind.
 */
function fits(entry: AreaClipLayer, layer: LayerDocument | undefined): layer is LayerDocument {
  return layer !== undefined && layer.kind === entry.kind;
}

/**
 * A reference value with its ids put through a map of old to new ids.
 *
 * @param value The field's value.
 * @param list Whether the field is a list of references.
 * @param copies The new id of each pasted thing, by its old id.
 * @returns The value with every id that names a pasted thing replaced by its copy's.
 */
function remapped(value: JsonValue | undefined, list: boolean, copies: ReadonlyMap<string, string>): JsonValue | undefined {
  if (list) {
    return Array.isArray(value) ? value.map((id) => (typeof id === "string" ? (copies.get(id) ?? id) : id)) : value;
  }
  return typeof value === "string" ? (copies.get(value) ?? value) : value;
}

/**
 * A thing's fields with every reference to a pasted thing pointed at its copy.
 *
 * @param schema The schema of the thing's registered type, absent when the game does not register it.
 * @param fields The thing's fields.
 * @param copies The new id of each pasted thing, by its old id.
 * @returns The fields, the same object when nothing changed.
 */
function pointedAtCopies<F extends { readonly [key: string]: JsonValue }>(schema: JsonSchema | undefined, fields: F, copies: ReadonlyMap<string, string>): F {
  if (schema === undefined) {
    return fields;
  }
  let next: { [key: string]: JsonValue } | null = null;
  for (const field of referenceFieldsOf(schema)) {
    const was = fields[field.name];
    const now = remapped(was, field.list === true, copies);
    if (now !== undefined && JSON.stringify(now) !== JSON.stringify(was)) {
      next = { ...(next ?? fields), [field.name]: now };
    }
  }
  return next === null ? fields : (next as F);
}

/**
 * What pasting one grid layer's block does: the commands' cells and the destination cells whose paint it would
 * change.
 */
interface BlockPlan {
  readonly command: Command | null;
  readonly changes: number;
  readonly replaced: readonly number[];
}

/**
 * Plans the paste of one grid layer's block at a destination rectangle.
 *
 * @param map The map.
 * @param world The world, whose tilesets name a packed tile's tileset.
 * @param layer The layer.
 * @param entry The clipboard's block for it.
 * @param destination The destination rectangle of tiles, inside the map.
 * @returns The command, if any cell changes, how many cells change and the cells whose paint is in the way.
 */
function planBlock(map: MapDocument, world: WorldDocument, layer: GridLayer, entry: AreaClipLayer, destination: AreaRect): BlockPlan {
  const raw = rawGridOf(layer);
  const grid: AreaGrid = raw.grid;
  const { width } = areaGridSize(map, grid);
  const bounds = areaGridBounds(destination, grid);
  const replaced: number[] = [];
  const tiles: TileCellChange[] = [];
  const bytes: CellChange[] = [];
  const zones: ZoneCellChange[] = [];
  let position = 0;
  eachCell(bounds, width, (index, x, y) => {
    const at = position;
    position += 1;
    if (layer.kind === "zone") {
      const to = entry.kind === "zone" ? (entry.cells[at] ?? null) : null;
      const was = zoneTypeAt(layer, index);
      if (was !== to) {
        zones.push({ index, before: was, after: to });
        if (was !== null) {
          replaced.push(index);
        }
      }
      return;
    }
    const held = raw.cells[index] as number;
    let after = held;
    if (entry.kind === "tile") {
      after = tileRefToPacked(entry.tiles[at] ?? null, world.tilesets);
    } else if (entry.kind === "terrain") {
      const block = entry.corners[at] as number;
      const ring = x === bounds.x0 || x === bounds.x1 || y === bounds.y0 || y === bounds.y1;
      after = !ring || block !== 0 ? block : held;
    } else if (entry.kind === "collision") {
      after = entry.cells[at] as number;
    }
    if (after !== held) {
      if (layer.kind === "tile") {
        tiles.push({ index, before: packedToTileRef(held, world.tilesets), after: packedToTileRef(after, world.tilesets) });
      } else {
        bytes.push({ index, before: held, after });
      }
      if (held !== raw.empty) {
        replaced.push(index);
      }
    }
  });
  const changes = tiles.length + bytes.length + zones.length;
  if (changes === 0) {
    return { command: null, changes: 0, replaced };
  }
  const mapId = map.id;
  const command: Command =
    layer.kind === "tile"
      ? { kind: "setTiles", mapId, layerId: layer.id, cells: tiles }
      : layer.kind === "terrain"
        ? { kind: "paintCorners", mapId, layerId: layer.id, cells: bytes }
        : layer.kind === "collision"
          ? { kind: "paintCollision", mapId, layerId: layer.id, cells: bytes }
          : { kind: "paintZone", mapId, layerId: layer.id, cells: zones };
  return { command, changes, replaced };
}

/**
 * The props and markers that stand at the destination, on any layer, which a paste leaves where they are.
 *
 * @param map The map.
 * @param layerIds The layers taken.
 * @param destination The destination rectangle.
 * @returns The things, in layer order.
 */
function keptAt(map: MapDocument, layerIds: ReadonlySet<string>, destination: AreaRect): AreaKept {
  const props: AreaKeptThing[] = [];
  const markers: AreaKeptThing[] = [];
  for (const layer of map.layers) {
    const taken = layerIds.has(layer.id);
    if (layer.kind === "prop") {
      props.push(...layer.props.filter((p) => anchoredIn(destination, p.x, p.y)).map((p) => ({ layerId: layer.id, id: p.id, taken })));
    } else if (layer.kind === "entity") {
      markers.push(...layer.entities.filter((e) => anchoredIn(destination, e.x, e.y)).map((e) => ({ layerId: layer.id, id: e.id, taken })));
    }
  }
  return { props, markers };
}

/**
 * Checks the ids a paste was handed: the right number, each different and none held by the world.
 *
 * @param world The world.
 * @param made The ids handed out.
 * @param count How many were asked for.
 * @param what What they name, for the message.
 * @throws RangeError when the allocator handed out a different number than asked for; Error when an id repeats or the
 *   world already holds it.
 */
function assertFresh(world: WorldDocument, made: readonly string[], count: number, what: string): void {
  if (made.length !== count) {
    throw new RangeError(`the allocator handed out ${made.length} ${what} ids for ${count} pasted things`);
  }
  const held = new Set<string>();
  for (const map of world.maps) {
    (map.groups ?? []).forEach((g) => held.add(g.id));
    for (const layer of map.layers) {
      held.add(layer.id);
      if (layer.kind === "prop") {
        layer.props.forEach((p) => held.add(p.id));
      } else if (layer.kind === "entity") {
        layer.entities.forEach((e) => held.add(e.id));
      }
    }
  }
  const seen = new Set<string>();
  for (const id of made) {
    if (held.has(id) || seen.has(id)) {
      throw new Error(`the allocator handed out ${what} id ${id}, which ${held.has(id) ? "the world already holds" : "it handed out twice"}`);
    }
    seen.add(id);
  }
}

/**
 * Plans the paste of a clipboard.
 *
 * @param world The world as it stands when the paste is confirmed.
 * @param mapId The map in view, which is the map the clipboard was taken from.
 * @param clipboard The clipboard.
 * @param at The block's top-left tile.
 * @param layerIds The layers the reach takes (`areaReach`); only these are written.
 * @param ids What mints the pasted things' ids.
 * @param types The registered types, whose schemas say which fields are references.
 * @returns The plan.
 * @throws Error when the map is missing, the clipboard is of another map or the allocator hands out an id the world
 *   holds or one twice; RangeError when the place is not in whole tiles or the allocator hands out the wrong number.
 */
export function planAreaPaste(world: WorldDocument, mapId: string, clipboard: AreaClipboard, at: { readonly x: number; readonly y: number }, layerIds: readonly string[], ids: AreaIds, types: ReferenceTypes): AreaPastePlan {
  const map = areaMapOf(world, mapId);
  if (clipboard.mapId !== mapId) {
    throw new Error(`the clipboard was taken from map ${clipboard.mapId}, not ${mapId}; paste across maps is not built`);
  }
  if (!Number.isInteger(at.x) || !Number.isInteger(at.y)) {
    throw new RangeError(`a paste is placed on whole tiles, not ${at.x}, ${at.y}`);
  }
  const taken = new Set(layerIds);
  const entries: { readonly entry: AreaClipLayer; readonly layer: LayerDocument }[] = [];
  const left: string[] = [];
  for (const entry of clipboard.layers) {
    const layer = map.layers.find((l) => l.id === entry.layerId);
    if (taken.has(entry.layerId) && fits(entry, layer)) {
      entries.push({ entry, layer });
    } else {
      left.push(entry.layerId);
    }
  }
  if (entries.length === 0) {
    return { kind: "nothing" };
  }
  const destination: AreaRect = { x0: at.x, y0: at.y, x1: at.x + clipboard.width - 1, y1: at.y + clipboard.height - 1 };
  const edge = edgeOf(map, destination);
  if (edge !== null) {
    return { kind: "refused", edge };
  }
  const kept = keptAt(map, taken, destination);
  const grids: Command[] = [];
  const items: Command[] = [];
  const replaced: AreaReplaced[] = [];
  const written = new Set<string>();
  let changes = 0;
  const propEntries = entries.filter((e) => e.entry.kind === "prop");
  const markerEntries = entries.filter((e) => e.entry.kind === "entity");
  const propCount = propEntries.reduce((n, e) => n + (e.entry.kind === "prop" ? e.entry.props.length : 0), 0);
  const markerCount = markerEntries.reduce((n, e) => n + (e.entry.kind === "entity" ? e.entry.entities.length : 0), 0);
  const propIds = propCount === 0 ? [] : ids("prop", propCount);
  const markerIds = markerCount === 0 ? [] : ids("entity", markerCount);
  if (propCount > 0) {
    assertFresh(world, propIds, propCount, "prop");
  }
  if (markerCount > 0) {
    assertFresh(world, markerIds, markerCount, "marker");
  }
  const copies = new Map<string, string>();
  let nextProp = 0;
  let nextMarker = 0;
  for (const { entry } of entries) {
    if (entry.kind === "prop") {
      entry.props.forEach((p) => copies.set(p.prop.id, propIds[nextProp++] as string));
    } else if (entry.kind === "entity") {
      entry.entities.forEach((e) => copies.set(e.entity.id, markerIds[nextMarker++] as string));
    }
  }
  let props = 0;
  let markers = 0;
  for (const layer of map.layers) {
    const found = entries.find((e) => e.layer.id === layer.id);
    if (found === undefined) {
      continue;
    }
    const { entry } = found;
    const before = grids.length + items.length;
    if (entry.kind === "prop" && layer.kind === "prop") {
      entry.props.forEach(({ dx, dy, prop }, n) => {
        const type = prop.behaviour === undefined ? undefined : types.objectTypes.find((t) => t.type === prop.behaviour?.type);
        const behaviour = prop.behaviour === undefined ? undefined : { ...prop.behaviour, fields: pointedAtCopies(type?.schema, prop.behaviour.fields, copies) };
        const made: PropInstance = { ...prop, id: copies.get(prop.id) as string, x: at.x + dx, y: at.y + dy, ...(behaviour === undefined ? {} : { behaviour }) };
        items.push({ kind: "addProp", mapId, layerId: layer.id, index: layer.props.length + n, prop: made });
        props += 1;
      });
    } else if (entry.kind === "entity" && layer.kind === "entity") {
      entry.entities.forEach(({ dx, dy, entity }, n) => {
        const type = types.entityTypes.find((t) => t.type === entity.type);
        const made: EntityInstance = { ...entity, id: copies.get(entity.id) as string, x: at.x + dx, y: at.y + dy, fields: pointedAtCopies(type?.schema, entity.fields, copies) };
        items.push({ kind: "addEntity", mapId, layerId: layer.id, index: layer.entities.length + n, entity: made });
        markers += 1;
      });
    } else if (isGridLayer(layer)) {
      const plan = planBlock(map, world, layer, entry, destination);
      if (plan.replaced.length > 0) {
        replaced.push({ layerId: layer.id, grid: rawGridOf(layer).grid, indices: plan.replaced });
      }
      if (plan.command !== null) {
        grids.push(plan.command);
        changes += plan.changes;
      }
    }
    if (grids.length + items.length > before) {
      written.add(layer.id);
    }
  }
  if (grids.length + items.length === 0) {
    return { kind: "nothing" };
  }
  return { kind: "paste", command: { kind: "compound", commands: [...grids, ...items] }, layers: map.layers.filter((l) => written.has(l.id)).map((l) => l.id), changes, props, markers, replaced, kept, left };
}
